import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Sparkles, Image as ImageIcon, CheckCircle, AlertTriangle, AlertCircle, Bell } from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { formatDistanceToNow } from "date-fns"
import { es } from "date-fns/locale"
import { cn } from "@/lib/utils"
import type { Notification } from "@/hooks/use-notifications"

interface NotificationDetailDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    notification: Notification | null
}

export function NotificationDetailDialog({ open, onOpenChange, notification }: NotificationDetailDialogProps) {
    if (!notification) return null

    // Detectar si es un changelog por el título
    const isChangelog = notification.title.includes('Actualización') || notification.title.includes('Novedades') || notification.title.includes('v1.')

    // Lógica de renderizado para Changelogs (Mantenemos la lógica original de ChangelogDialog)
    const renderChangelogContent = () => {
        const version = notification.title.includes('v1.5') ? '1.5' : '1.x'

        // Parse features from message
        const features = notification.message
            .split('\n')
            .filter((line: string) => line.trim().startsWith('•') || line.trim().startsWith('-'))
            .map((line: string) => {
                // Remove bullet point
                const cleanLine = line.replace(/^[•-]\s*/, '')

                // Match: [Icon] [Title]: [Description] (Optional icon)
                // Intentamos capturar emojis o iconos al principio
                const match = cleanLine.match(/^(\P{L}+)?\s*(.+?):\s*(.+)/u)

                if (match) {
                    const icon = match[1]?.trim() || '✨'
                    const title = match[2].replace(/\*\*/g, '').trim()
                    const description = match[3].trim()
                    return { icon, title, description }
                }

                // Fallback si no tiene formato estricto "Titulo: Descripcion"
                return { icon: '✨', title: '', description: cleanLine }
            })

        return (
            <div className="space-y-6 py-4">
                <DialogDescription className="text-base leading-relaxed">
                    Nuevas funcionalidades y mejoras disponibles:
                </DialogDescription>

                {features.length > 0 ? (
                    <div className="space-y-4">
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary" />
                            Novedades Destacadas
                        </h3>

                        <div className="grid gap-4">
                            {features.map((feature: { icon: string; title: string; description: string }, index: number) => (
                                <div
                                    key={index}
                                    className="flex gap-4 p-4 rounded-xl bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
                                >
                                    <div className="text-2xl shrink-0">{feature.icon}</div>
                                    <div className="flex-1">
                                        {feature.title && <strong className="font-semibold block mb-0.5">{feature.title}</strong>}
                                        <p className="text-sm leading-relaxed text-muted-foreground">{feature.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="p-4 bg-muted/30 rounded-lg whitespace-pre-wrap font-mono text-sm">
                        {notification.message}
                    </div>
                )}

                {/* Additional sections based on version - Hardcoded logic for v1.5 promotion */}
                {version === "1.5" && (
                    <div className="space-y-4 pt-4 border-t">
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                            <ImageIcon className="w-5 h-5 text-primary" />
                            Cómo Usar las Nuevas Funciones
                        </h3>

                        <div className="space-y-3 text-sm text-muted-foreground">
                            <div className="p-3 bg-primary/5 rounded-lg border-l-4 border-primary">
                                <p className="font-medium text-foreground mb-1">📸 Subir Logo de Organización</p>
                                <p>Ve a <strong>Perfil → Organización</strong> y haz clic en "Subir logo". Solo administradores y propietarios pueden gestionar el logo.</p>
                            </div>

                            <div className="p-3 bg-primary/5 rounded-lg border-l-4 border-primary">
                                <p className="font-medium text-foreground mb-1">🤖 Análisis de Imágenes en Soporte</p>
                                <p>Abre el chatbot de soporte, haz clic en el ícono de clip 📎 y adjunta una captura de pantalla. El asistente analizará la imagen para darte ayuda contextual.</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    // Lógica de renderizado para notificaciones normales
    const renderGenericContent = () => {
        // Seleccionar icono basado en tipo (si existe en el objeto notification)
        const getIcon = () => {
            switch (notification.type) {
                case 'success': return <CheckCircle className="h-10 w-10 text-green-500 mb-4" />
                case 'warning': return <AlertTriangle className="h-10 w-10 text-yellow-500 mb-4" />
                case 'alert': return <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
                default: return <Bell className="h-10 w-10 text-primary mb-4" />
            }
        }

        return (
            <div className="flex flex-col items-center text-center py-6 space-y-4">
                {getIcon()}

                <div className="space-y-2 w-full text-left bg-muted/30 p-4 rounded-xl border">
                    <p className="text-base text-foreground leading-relaxed whitespace-pre-wrap">
                        {notification.message}
                    </p>
                </div>

                <div className="w-full text-right">
                    <span className="text-xs text-muted-foreground">
                        Recibido {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: es })}
                    </span>
                </div>
            </div>
        )
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] sm:rounded-2xl p-0 flex flex-col overflow-hidden shadow-2xl border-primary/10">
                <DialogHeader className={cn("px-8 pt-8 pb-4 shrink-0", !isChangelog ? "border-b" : "")}>
                    <div className="flex items-center gap-3">
                        {isChangelog && (
                            <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                                <Sparkles className="w-6 h-6 text-primary" />
                            </div>
                        )}
                        <div>
                            <DialogTitle className="text-xl sm:text-2xl break-words text-left pr-8">
                                {notification.title}
                            </DialogTitle>
                            {isChangelog && (
                                <p className="text-sm text-muted-foreground mt-1 text-left">
                                    Detalles de la versión
                                </p>
                            )}
                        </div>
                    </div>
                </DialogHeader>

                <ScrollArea className="flex-1 px-8">
                    <div className="py-6 space-y-6">
                        {isChangelog ? renderChangelogContent() : renderGenericContent()}
                        <div className="h-10 shrink-0" /> {/* Extra space at bottom */}
                    </div>
                </ScrollArea>

            </DialogContent>
        </Dialog>
    )
}
